/**
 * Map Types for Blurp.ie Mapbox Integration
 */

import type { Coordinates } from './geolocation';

export interface MapViewport {
  longitude: number;
  latitude: number;
  zoom: number;
  pitch: number;
  bearing: number;
}

export interface MapBounds {
  ne: Coordinates;
  sw: Coordinates;
}

export interface PlumeDataPoint {
  coordinates: Coordinates;
  value: number; // Concentration value
  pollutantType: PollutantType;
  radius: number; // meters
  timestamp: number;
}

export type PollutantType = 'pm25' | 'pm10' | 'ozone' | 'co2' | 'no2' | 'so2';

export interface AirQualityGeoJSON {
  type: 'FeatureCollection';
  features: AirQualityFeature[];
}

export interface AirQualityFeature {
  type: 'Feature';
  geometry: {
    type: 'Point';
    coordinates: [number, number]; // [lng, lat]
  };
  properties: {
    value: number;
    pollutantType: PollutantType;
    status: string;
    timestamp: number;
  };
}

export interface RouteGeoJSON {
  type: 'Feature';
  geometry: {
    type: 'LineString';
    coordinates: [number, number][]; // Array of [lng, lat]
  };
  properties: {
    safetyLevel: 'safe' | 'moderate' | 'hazardous';
    cognitiveScore: number;
    segmentScores?: number[];
  };
}

export interface ThreatZoneGeoJSON {
  type: 'Feature';
  geometry: {
    type: 'Polygon';
    coordinates: [number, number][][]; // Polygon rings
  };
  properties: {
    threatLevel: 'low' | 'medium' | 'high' | 'critical';
    pollutantType: PollutantType;
    value: number;
    name?: string;
  };
}

export interface MapLayerVisibility {
  plumePM25: boolean;
  plumeOzone: boolean;
  plumeCO2: boolean;
  safeRoute: boolean;
  threatZones: boolean;
  buildings3D: boolean;
  userLocation: boolean;
}

export interface MapStyle {
  id: string;
  name: string;
  url: string;
}

// Blurp.ie custom map styles
export const MAP_STYLES: MapStyle[] = [
  {
    id: 'blurp-dark',
    name: 'Neural Dark',
    url: 'mapbox://styles/mapbox/dark-v11',
  },
  {
    id: 'blurp-satellite',
    name: 'Satellite',
    url: 'mapbox://styles/mapbox/satellite-streets-v12',
  },
];

// Default viewport for initial map load
export const DEFAULT_VIEWPORT: MapViewport = {
  longitude: 101.6869, // Kuala Lumpur
  latitude: 3.139,
  zoom: 14,
  pitch: 45,
  bearing: 0,
};

// Color scales for plume visualization
export const PLUME_COLORS = {
  pm25: {
    low: [0, 255, 136, 0.3], // Green
    mid: [255, 221, 0, 0.5], // Yellow
    high: [255, 51, 102, 0.7], // Red
  },
  ozone: {
    low: [0, 204, 230, 0.3], // Cyan
    mid: [128, 0, 255, 0.5], // Purple
    high: [255, 0, 170, 0.7], // Magenta
  },
  co2: {
    low: [128, 128, 128, 0.2], // Gray
    mid: [179, 128, 77, 0.4], // Brown
    high: [77, 51, 26, 0.6], // Dark brown
  },
} as const;
